import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getTrackingData } from "../../utils/trackingConfig";
import { subscriptionData } from "../../redux/slices/subscriptionSlice";
import { subscriptionPayment } from "../../redux/slices/subscriptionPaymentSlice";
import usePlan from "../../hooks/usePlan";
import "./MessagingUpgradeModal.scss";

const MessageIcon = (props) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

const SparkIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M12 2 13.6 8.4 20 10l-6.4 1.6L12 18l-1.6-6.4L4 10l6.4-1.6z" />
  </svg>
);

const MessagingUpgradeModal = ({ show, eventSource }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isTrialEligible: trialEligible } = usePlan();
  const subscriptionState = useSelector((state) => state.subscriptionData);
  const authStore = useSelector((state) => state.auth);
  const [isCheckingOut, setIsCheckingOut] = useState(false);

  const handleStartTrial = () => {
    navigate("/trial", { state: { source: eventSource } });
  };

  const handleUpgrade = () => {
    navigate("/profile/subscription", {
      state: { source: eventSource },
    });
  };

  const handleSkipTrial = async () => {
    let plans = subscriptionState?.subscriptionDataItems || [];
    if (!plans.length) {
      try {
        const result = await dispatch(subscriptionData()).unwrap();
        plans = result?.subscriptionData || [];
      } catch (e) {
        handleUpgrade();
        return;
      }
    }
    const proMonthlyPlan = plans.find(
      (p) =>
        p.name === "Pro" &&
        p.interval === "month" &&
        p.slug !== "pro-newsletter",
    );
    if (!proMonthlyPlan?.price_id) {
      handleUpgrade();
      return;
    }
    const { event } = getTrackingData(eventSource);
    const payload = {
      priceId: proMonthlyPlan.price_id,
      customerEmail: authStore?.user?.email,
      metadata: {
        userId: authStore?.user?.id,
        plan_id: proMonthlyPlan.id,
        billingCycle: "month",
        trigger_feature: event,
      },
    };
    setIsCheckingOut(true);
    try {
      const result = await dispatch(subscriptionPayment(payload)).unwrap();
      const checkoutUrl =
        result?.subscriptionPaymentItems?.url ||
        result?.url ||
        result?.data?.url;
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
      } else {
        handleUpgrade();
      }
    } catch (error) {
      console.error("Skip trial checkout failed:", error);
      handleUpgrade();
    } finally {
      setIsCheckingOut(false);
    }
  };

  const handleRefer = () => {
    navigate("/referral");
  };

  if (!show) return null;

  return (
    <div className="messaging-upgrade-overlay">
      <div className="messaging-upgrade-backdrop-custom"></div>
      <div className="messaging-upgrade-modal-wrapper">
        {/* Mirrors Bootstrap's .modal-dialog.upgrade-pro-modal > .modal-content > .modal-body
            so ComparisonModal.scss upm-* rules apply identically */}
        <div className="upgrade-pro-modal">
          <div className="modal-content">
            <div className="modal-body">
              {/* No close button — free users cannot dismiss this modal */}

              <div className="upm-icon-row">
                <div className="upm-icon">
                  <MessageIcon className="upm-icon-svg" />
                </div>
                <span className="upm-badge-limit">Pro Feature</span>
              </div>

              <h2 className="upm-title">
                {trialEligible
                  ? "Messaging is a Pro feature"
                  : "Messaging is a Pro feature"}
              </h2>
              <p className="upm-sub">
                Upgrade to connect directly with other nurses.
              </p>

              <div className="upm-info">
                <SparkIcon className="upm-info-spark" />
                <p className="upm-info-text">
                  {trialEligible
                    ? "Pro users can message other nurses directly"
                    : "Pro users can message other nurses directly"}
                </p>
              </div>

              <div className="upm-ctas">
                {trialEligible ? (
                  <button
                    type="button"
                    className="upm-btn-primary"
                    onClick={handleStartTrial}
                  >
                    <span className="upm-btn-primary-title">Start 3-day free trial</span>
                    <span className="upm-btn-primary-sub">
                      No charge today. $29.99/year after your 3-day trial.
                    </span>
                  </button>
                ) : (
                  <button
                    type="button"
                    className="upm-btn-primary"
                    onClick={handleUpgrade}
                  >
                    <span className="upm-btn-primary-title">Get Pro</span>
                    <span className="upm-btn-primary-sub">
                      $2.99/month, billed annually at $29.99/year. Cancel anytime.
                    </span>
                  </button>
                )}

                <button
                  type="button"
                  className="upm-btn-secondary"
                  onClick={handleSkipTrial}
                  disabled={isCheckingOut}
                >
                  {isCheckingOut ? "Opening Secure Payment..." : "Buy Pro Plan · $2.99/month"}
                </button>

                <button
                  type="button"
                  className="upm-newsletter"
                  onClick={handleRefer}
                >
                  <span className="upm-newsletter-q">Want jobs in your inbox too?</span>
                  <span className="upm-newsletter-cta">
                    Get Pro + Newsletter, $4.99/month billed annually at $49.99/year
                  </span>
                </button>
              </div>

              {/* No "Maybe later" — free users must navigate away to close */}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessagingUpgradeModal;
